import React, { useState } from 'react';
import { Box, Text, InputGroup, InputLeftElement, Input, SimpleGrid } from '@chakra-ui/react';
import { UgcRatePreset } from '../../../utils/contentUtils';
import ModalShell from '../../../components/ui/ModalShell';
import { ff } from '../../../theme';

interface AddRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableOptions: UgcRatePreset[];
  onAdd: (preset: UgcRatePreset, price: number) => void;
}

const AddRateModal: React.FC<AddRateModalProps> = ({
  isOpen,
  onClose,
  availableOptions,
  onAdd,
}) => {
  const [selectedService, setSelectedService] = useState<UgcRatePreset | null>(null);
  const [priceInput, setPriceInput] = useState('');

  const handleClose = () => {
    setSelectedService(null);
    setPriceInput('');
    onClose();
  };

  const handleSubmit = () => {
    const price = Number(priceInput);
    if (!selectedService || Number.isNaN(price) || price < 0) return;
    onAdd(selectedService, price);
    setSelectedService(null);
    setPriceInput('');
  };

  const canSubmit =
    availableOptions.length > 0 &&
    selectedService !== null &&
    priceInput !== '' &&
    Number(priceInput) >= 0;

  const hasOptions = availableOptions.length > 0;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title="Add rate"
      cancelLabel={hasOptions ? 'Cancel' : undefined}
      primary={hasOptions ? { label: 'Add rate', onClick: handleSubmit, tone: 'lime', isDisabled: !canSubmit } : undefined}
    >
      {!hasOptions ? (
        <Box py={6} textAlign="center">
          <Text fontSize="15px" color={ff.text2}>
            All rate types have been added. Remove one to add it again.
          </Text>
        </Box>
      ) : (
        <>
          <Text textStyle="eyebrow" color={ff.text2} mb={3}>
            Type
          </Text>
          <SimpleGrid columns={2} spacing={2} mb={6}>
            {availableOptions.map((opt) => {
              const label = opt.displayName;
              const isSelected = selectedService?.displayName === opt.displayName;
              return (
                <Box
                  key={label}
                  as="button"
                  type="button"
                  onClick={() => setSelectedService(opt)}
                  minH="48px"
                  px={3}
                  py={2}
                  borderRadius="14px"
                  border="1px solid"
                  borderColor={isSelected ? ff.ink : ff.border}
                  bg={isSelected ? ff.ink : 'white'}
                  color={isSelected ? ff.lime : ff.ink}
                  fontFamily="display"
                  fontSize="14px"
                  fontWeight={isSelected ? 600 : 500}
                  textAlign="center"
                  transition="all 0.15s"
                  _hover={{ borderColor: ff.ink }}
                >
                  {label}
                </Box>
              );
            })}
          </SimpleGrid>

          <Text textStyle="eyebrow" color={ff.text2} mb={3}>
            Price
          </Text>
          <InputGroup>
            <InputLeftElement pointerEvents="none" h="48px">
              <Text color={ff.text2} fontWeight={600} fontSize="17px">$</Text>
            </InputLeftElement>
            <Input
              type="number"
              min={0}
              step={1}
              placeholder="0"
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
              fontSize="17px"
              fontWeight={500}
            />
          </InputGroup>
        </>
      )}
    </ModalShell>
  );
};

export default AddRateModal;
